import type { ReactNode } from 'react';
import { useSyncExternalStore } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAccessToken, subscribeAuthState } from '@beatq/shared';
import {
  branchesFromEnvelope,
  listBranches,
} from '../features/branch-settings/branchSettingsApi.js';
import {
  BRANCH_SETUP_PARAM,
  isOnboardingSetupPath,
} from '../features/onboarding/branchSetupState.js';
import { onboardingChecklistPath } from '../features/onboarding/onboardingState.js';
import { PermissionDeniedFrame } from './MerchantEmptyStates.js';
import { merchantIsPreLive } from './merchantPreLive.js';
import {
  filterMerchantNavForRoles,
  getMerchantRoleCodes,
  isMerchantNavSectionVisible,
  merchantHomeHref,
  resolveMerchantNavSection,
} from './merchantNavAccess.js';

export function MerchantSectionGate({ children }: { children: ReactNode }) {
  useSyncExternalStore(subscribeAuthState, getAccessToken, () => null);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const roles = getMerchantRoleCodes();
  const section = resolveMerchantNavSection(location.pathname);
  const branchesQuery = useQuery({
    queryKey: ['merchant', 'branches'],
    queryFn: listBranches,
  });
  const branches = branchesFromEnvelope(branchesQuery.data);
  const preLive = merchantIsPreLive(branches);
  const isSetupSpine =
    searchParams.get(BRANCH_SETUP_PARAM) === '1' && isOnboardingSetupPath(location.pathname);
  const isHostedPaymentSetup =
    location.pathname === '/beat-pay-setup' || location.pathname === '/beat-pay-return';
  // Empty `[]` while the query is pending looks pre-live and bounces operational
  // routes to the checklist (which then sends live merchants to the dashboard).
  const branchesReady = !branchesQuery.isPending && !branchesQuery.isError;

  if (branchesReady && preLive && location.pathname === '/finance/beat-pay') {
    return <Navigate to="/beat-pay-setup" replace />;
  }

  if (branchesReady && preLive && !isSetupSpine && !isHostedPaymentSetup) {
    return <Navigate to={onboardingChecklistPath()} replace />;
  }

  if (section && roles.length > 0 && !isMerchantNavSectionVisible(section, roles)) {
    const visible = filterMerchantNavForRoles(roles);
    const backTo = merchantHomeHref(roles);
    const backLabel = visible[0]?.label != null ? `Back to ${visible[0].label}` : 'Back to home';

    return (
      <PermissionDeniedFrame
        screenId="SCR-MER-NAV-GATE"
        title="Section unavailable"
        message="This area is not available for your merchant role."
        backTo={backTo}
        backLabel={backLabel}
      />
    );
  }

  return children;
}
